import type React from "react";

type FocusArea = {
  title: string;
  description: string;
};

type MissionSectionProps = {
  mission: string;
  focusAreas: FocusArea[];
};

const MissionSection: React.FC<MissionSectionProps> = ({
  mission,
  focusAreas,
}) => {
  return (
    <section
      id="mission"
      className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-white py-24"
    >
      <div className="pointer-events-none absolute -left-16 top-10 hidden h-40 w-40 rounded-full bg-primary-200/50 blur-3xl lg:block" />
      <div className="pointer-events-none absolute -right-20 bottom-0 hidden h-56 w-56 rounded-full bg-primary-100/60 blur-3xl md:block" />

      <div className="container relative grid gap-14 lg:grid-cols-[1.05fr_minmax(0,1fr)]">
        <div className="flex flex-col gap-6 rounded-3xl border border-primary-900/10 bg-white/80 p-10 shadow-xl backdrop-blur">
          <span className="inline-flex w-fit items-center rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-primary-700">
            Mission & approach
          </span>
          <h2 className="text-3xl font-semibold text-primary-900 sm:text-4xl">
            Centered on community resilience
          </h2>
          <p className="text-lg leading-relaxed text-neutral-700">{mission}</p>

          <div className="grid gap-3 text-sm text-neutral-700">
            <div className="rounded-2xl border border-primary-900/10 bg-primary-50/70 px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-[0.28em] text-primary-700/80">
                What guides us
              </div>
              <p className="mt-2 text-sm">
                Rooted in collaboration, we align resources, partners, and
                volunteers to amplify local impact.
              </p>
            </div>
            <div className="rounded-2xl border border-primary-900/10 bg-white px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-[0.28em] text-primary-700/80">
                Strategic focus
              </div>
              <p className="mt-2 text-sm">
                Every initiative is paired with capacity-building support so
                communities can sustain progress long term.
              </p>
            </div>
            <div className="rounded-2xl border border-primary-900/10 bg-white px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-[0.28em] text-primary-700/80">
                Accountability
              </div>
              <p className="mt-2 text-sm">
                We track outcomes with partners to ensure investments translate
                into measurable change for families.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-primary-900 sm:text-2xl">
              Program pillars
            </h3>
            <span className="text-sm font-medium uppercase tracking-[0.28em] text-primary-600">
              {focusAreas.length.toString().padStart(2, "0")}
              {focusAreas.length === 1 ? " Area" : " Areas"}
            </span>
          </div>

          <ol className="grid gap-5">
            {focusAreas.map((area, index) => (
              <li
                key={area.title}
                className="relative overflow-hidden rounded-3xl border border-primary-900/10 bg-white/70 p-6 shadow-sm"
              >
                <div className="absolute right-6 top-6 text-4xl font-semibold text-primary-100 group-hover:text-primary-200">
                  {(index + 1).toString().padStart(2, "0")}
                </div>
                <div className="relative flex flex-col gap-3">
                  <span className="w-fit rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-primary-600">
                    Pillar {(index + 1).toString().padStart(2, "0")}
                  </span>
                  <h4 className="text-xl font-semibold text-primary-800">
                    {area.title}
                  </h4>
                  <p className="text-sm leading-relaxed text-neutral-700">
                    {area.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export default MissionSection;
